import React, { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "ec_visit_counted";

/**
 * Simple, subtle public visit counter.
 *
 * Renders two numbers: the all-time total and a rolling 30-day window.
 * On first render in a browser session this component POSTs once to
 * /api/visits to increment the counter, then reads it back. Subsequent
 * renders within the same session (in-tab navigation, route changes) skip
 * the POST and just GET so reloads-of-a-reload don't inflate the count.
 *
 * sessionStorage clears when the tab closes — so a real "new visit" is
 * actually a new tab session, which is the most honest definition we can
 * cheaply produce without cookies or fingerprinting.
 */
export default function VisitCounter() {
    const [counts, setCounts] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const alreadyCounted =
            typeof window !== "undefined" &&
            window.sessionStorage?.getItem(STORAGE_KEY) === "1";

        async function load() {
            try {
                const url = `${API}/visits`;
                const { data } = alreadyCounted
                    ? await axios.get(url)
                    : await axios.post(url);
                if (cancelled) return;
                if (!alreadyCounted) {
                    try {
                        window.sessionStorage.setItem(STORAGE_KEY, "1");
                    } catch (_) {
                        /* Safari private mode etc. — non-fatal */
                    }
                }
                setCounts({
                    total:
                        typeof data?.count === "number" ? data.count : null,
                    last30:
                        typeof data?.last30 === "number" ? data.last30 : null,
                });
            } catch (_) {
                // Silently swallow: a missing counter must never break the
                // footer or surface an error to the visitor.
                if (!cancelled) setCounts(null);
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, []);

    if (!counts || counts.total === null) return null;
    return (
        <span
            data-testid="footer-visit-counter"
            className="inline-flex flex-wrap items-baseline justify-center gap-x-3 gap-y-1 text-xs text-brand-muted"
        >
            <span>
                Visitors:{" "}
                <span
                    data-testid="footer-visit-counter-total"
                    className="font-mono text-brand-ivory"
                >
                    {counts.total.toLocaleString()}
                </span>
            </span>
            {counts.last30 !== null && (
                <span
                    aria-label={`${counts.last30} visitors in the last 30 days`}
                    className="text-brand-goldSoft"
                >
                    Last 30 days:{" "}
                    <span
                        data-testid="footer-visit-counter-last30"
                        className="font-mono text-brand-ivory/90"
                    >
                        {counts.last30.toLocaleString()}
                    </span>
                </span>
            )}
        </span>
    );
}
